import { Loader } from "@circle-react-uikit/Loader";
import { Portal } from "@circle-react-uikit/Portal";
import { Rail } from "@circle-react-uikit/Rail";
import { useRailbar } from "@circle-react/hooks/chatsV2/useRailbar";
import { SideDrawer } from "@circleco/compass/components/SideDrawer";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

import { useRailbarPortalContext } from "./RailbarPortalContext";

type RailbarPosition = "header" | "content" | "portal" | "drawer";

interface RailBarPortalProps {
  children: ReactNode;
  isFullWidth?: boolean;
  isLoading?: boolean;
  loaderVariant?: "rail-profile" | "rail-member-list";
  noBorderLeft?: boolean;
  position?: RailbarPosition;
  selectorQuery?: string;
}

export const RailBarPortal = ({
  children,
  isFullWidth,
  isLoading,
  loaderVariant,
  noBorderLeft,
  position = "header",
  selectorQuery,
}: RailBarPortalProps) => {
  const {
    shouldShowRailBar = true,
    isUnifiedInbox,
    toggleShowRailBar,
  } = useRailbar();
  const ctx = useRailbarPortalContext();

  if (selectorQuery) {
    return <Portal selectorQuery={selectorQuery}>{children}</Portal>;
  }

  if (isUnifiedInbox) {
    return (
      <SideDrawer.Root
        open={shouldShowRailBar}
        onOpenChange={toggleShowRailBar}
        size="sm"
        noBackdrop
      >
        <SideDrawer.Content>
          <div className="dark:bg-secondary flex h-full min-h-0 flex-col">
            {isLoading ? <Loader center /> : children}
          </div>
        </SideDrawer.Content>
      </SideDrawer.Root>
    );
  }

  // Use the context-scoped target (set by the nearest
  // <RailbarPortalProvider>) when one exists for this position. The
  // existence of a provider signals "I want to manage my own railbar
  // target" — never fall back to the global `#rail-bar-${position}`
  // in that case, or we race the AppShell's empty container and briefly
  // mount into the wrong rail on first render (causing a visible flash
  // and unmounting any state in the rail subtree once `registerTarget`
  // commits). Consumers outside any provider still hit the global DOM
  // fallback unchanged.
  const isContextManagedPosition =
    position === "header" || position === "content";
  const portalElement =
    ctx && isContextManagedPosition
      ? ctx.getTarget(position)
      : document.querySelector(`#rail-bar-${position}`);

  if (!portalElement || !shouldShowRailBar) return null;

  // `Rail`'s outer `<div>` is portaled directly into the target so the
  // shell's `[&_#rail-bar-header>div]:!w-full / :!h-full` overrides (in
  // `ShellRailContent`) hit `Rail` itself and force it to fill the panel.
  // Wrapping `Rail` in an extra `<div>` would shadow those selectors and
  // leave `Rail` stuck at its 360px inline width inside the AppShell.
  return createPortal(
    <Rail
      isFullWidth={isFullWidth}
      isLoading={isLoading}
      loaderVariant={loaderVariant}
      noBorderLeft={noBorderLeft}
    >
      {children}
    </Rail>,
    portalElement,
  );
};
